import type { ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import { MultiSelect } from '@/components/ui/MultiSelect'
import { Select } from '@/components/ui/Select'

/* ─── Main Shell ─── */

function FilterPanel({
  children,
  onReset,
  isLoading,
}: {
  children: ReactNode
  onReset: () => void
  isLoading?: boolean
}) {
  if (isLoading) {
    return <div className="animate-pulse bg-elevated rounded-lg h-64" />
  }

  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-primary">Filters</h3>
        <Button variant="ghost" size="sm" onClick={onReset} className="text-muted hover:text-primary px-0">
          Clear All
        </Button>
      </div>
      {children}
    </div>
  )
}

/* ─── Section with label ─── */

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-secondary mb-2">{label}</label>
      {children}
    </div>
  )
}

/* ─── Button group for toggle filters ─── */

const defaultActiveClass = 'bg-blue-600 border-blue-500 text-white'
const inactiveClass = 'bg-muted-bg border-theme-border-subtle text-secondary hover:bg-active'

function ButtonGroup({
  options,
  selected,
  onToggle,
  getActiveClass,
}: {
  options: string[]
  selected: string[]
  onToggle: (value: string) => void
  getActiveClass?: (value: string) => string
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => (
        <button
          key={option}
          onClick={() => onToggle(option)}
          className={`px-3 py-1 rounded text-sm font-medium border transition-all ${
            selected.includes(option) ? (getActiveClass?.(option) ?? defaultActiveClass) : inactiveClass
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  )
}

/* ─── Select dropdown ─── */

function SelectFilter({
  value,
  onChange,
  options,
  placeholder = 'All',
}: {
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  placeholder?: string
}) {
  return (
    <Select
      value={value}
      onChange={onChange}
      options={[{ value: '', label: placeholder }, ...options]}
      className="w-full"
    />
  )
}

/* ─── Multi-select dropdown ─── */

function MultiSelectFilter({
  value,
  onChange,
  options,
  placeholder = 'All',
}: {
  value: string[]
  onChange: (value: string[]) => void
  options: { value: string; label: string }[]
  placeholder?: string
}) {
  return (
    <MultiSelect value={value} onChange={onChange} options={options} placeholder={placeholder} className="w-full" />
  )
}

/* ─── Range inputs ─── */

function RangeFilter({
  valueMin,
  valueMax,
  onMinChange,
  onMaxChange,
  step,
  min,
  max,
}: {
  valueMin: number | undefined
  valueMax: number | undefined
  onMinChange: (val: number | undefined) => void
  onMaxChange: (val: number | undefined) => void
  step?: number
  min?: number
  max?: number
}) {
  return (
    <div className="flex gap-2">
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={valueMin ?? ''}
        onChange={(e) => onMinChange(e.target.value ? parseInt(e.target.value) : undefined)}
        placeholder="Min"
        className="input w-1/2"
      />
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={valueMax ?? ''}
        onChange={(e) => onMaxChange(e.target.value ? parseInt(e.target.value) : undefined)}
        placeholder="Max"
        className="input w-1/2"
      />
    </div>
  )
}

/* ─── Attach sub-components ─── */

FilterPanel.Section = Section
FilterPanel.ButtonGroup = ButtonGroup
FilterPanel.SelectFilter = SelectFilter
FilterPanel.MultiSelectFilter = MultiSelectFilter
FilterPanel.RangeFilter = RangeFilter

export { FilterPanel }
